/* eslint-disable react/jsx-no-literals */
/* eslint-disable complexity */
import React from 'react';

import Tooltip from 'carbon-react/lib/components/tooltip';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { RepeatingPattern } from '../../../types';
import {
  FORMULA_ACTIONS,
  getFormulaActionsPlaceholder
} from '../../../../../ui/data-builder/formula-content-form/formula-actions/const';

import * as styles from '../formula.scss';

interface Props {
  handleActionClick: (action: string) => void;
  outputFrequency: RepeatingPattern | null;
}

export const ActionButtons = ({ handleActionClick, outputFrequency }: Props) => {
  const intl = useIntl();

  return (
    <>
      <div className={styles.description}>
        <FormattedMessage
          id="reporting.type_at_for_account_category_group"
          defaultMessage="Type @ for account category, group, account, prediction and formula references."
        />
      </div>
      <div className={styles.actionsSection} onMouseDown={event => event.preventDefault()}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', width: 260, bottom: 36 }} className={styles.actionTitle}>
            <FormattedMessage id="reporting.use_after_at_reference" defaultMessage="Use after an @ reference" />
          </div>
          <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('+')}>
            +
          </button>
        </div>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('-')}>
          -
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('*')}>
          &times;
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('/')}>
          &divide;
        </button>
        {outputFrequency === RepeatingPattern.daily && (
          <>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthDaily])}
            >
              <Tooltip
                message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthDaily]}</div>}
                position="top"
              >
                <div>
                  <FormattedMessage id="formula.last_month" defaultMessage="LASTMONTH" />
                </div>
              </Tooltip>
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearDaily])}
            >
              <Tooltip
                message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearDaily]}</div>}
                position="top"
              >
                <div>
                  <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
                </div>
              </Tooltip>
            </button>
          </>
        )}
        {outputFrequency === RepeatingPattern.monthly && (
          <>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthMonthly])}
            >
              <Tooltip
                message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthMonthly]}</div>}
                position="top"
              >
                <div>
                  <FormattedMessage id="formula.last_month" defaultMessage="LASTMONTH" />
                </div>
              </Tooltip>
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearMonthly])}
            >
              <Tooltip
                message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearMonthly]}</div>}
                position="top"
              >
                <div>
                  <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
                </div>
              </Tooltip>
            </button>
          </>
        )}
        {outputFrequency === RepeatingPattern.annually && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearAnnually])}
          >
            <Tooltip
              message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearAnnually]}</div>}
              position="top"
            >
              <div>
                <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
              </div>
            </Tooltip>
          </button>
        )}
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => {
            if (outputFrequency === RepeatingPattern.daily) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateDaily]);
            } else if (outputFrequency === RepeatingPattern.monthly) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateMonthly]);
            } else {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateAnnually]);
            }
          }}
          style={{ marginRight: 20 }}
        >
          <Tooltip
            message={
              <div>
                {outputFrequency === RepeatingPattern.daily ? (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateDaily]}</>
                ) : outputFrequency === RepeatingPattern.monthly ? (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateMonthly]}</>
                ) : (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateAnnually]}</>
                )}
              </div>
            }
            position="top"
          >
            <div>
              <FormattedMessage id="formula.on_date" defaultMessage="ON DATE" />
            </div>
          </Tooltip>
        </button>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', width: 340, bottom: 36 }} className={styles.actionTitle}>
            <FormattedMessage id="predict.use_for_conditionals" defaultMessage="Use for conditionals" />
          </div>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.if])}
          >
            <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.if]}</div>} position="top">
              <div>
                <FormattedMessage id="formula.if" defaultMessage="IF" />
              </div>
            </Tooltip>
          </button>
        </div>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.year])}
        >
          <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.year]}</div>} position="top">
            <div>{intl.formatMessage({ id: 'formula.year', defaultMessage: 'year' }).toUpperCase()}</div>
          </Tooltip>
        </button>
        {(outputFrequency === RepeatingPattern.daily || outputFrequency === RepeatingPattern.monthly) && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.month])}
          >
            <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.month]}</div>} position="top">
              <div>{intl.formatMessage({ id: 'common.month', defaultMessage: 'month' }).toUpperCase()}</div>
            </Tooltip>
          </button>
        )}
        {outputFrequency === RepeatingPattern.daily && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dayOfMonth])}
          >
            <Tooltip
              message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dayOfMonth]}</div>}
              position="top"
            >
              <div>
                <FormattedMessage id="formula.day_of_month" defaultMessage="DAYOFMONTH" />
              </div>
            </Tooltip>
          </button>
        )}
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => {
            if (outputFrequency === RepeatingPattern.daily) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateDaily]);
            } else if (outputFrequency === RepeatingPattern.monthly) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateMonthly]);
            } else {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateAnnually]);
            }
          }}
        >
          <Tooltip
            message={
              <div>
                {outputFrequency === RepeatingPattern.daily ? (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateDaily]}</>
                ) : outputFrequency === RepeatingPattern.monthly ? (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateMonthly]}</>
                ) : (
                  <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateAnnually]}</>
                )}
              </div>
            }
            position="top"
          >
            <div>
              <FormattedMessage id="formula.date" defaultMessage="DATE" />
            </div>
          </Tooltip>
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('<')}>
          &lt;
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('>')}>
          &gt;
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('<=')}>
          &le;
        </button>
        <button type="button" className={styles.actionOperator} onClick={() => handleActionClick('>=')}>
          &ge;
        </button>
        <button
          type="button"
          className={styles.actionOperator}
          onClick={() => handleActionClick('=')}
          style={{ marginRight: 60 }}
        >
          =
        </button>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', width: 150, bottom: 36 }} className={styles.actionTitle}>
            <FormattedMessage id="predict.functions" defaultMessage="Functions" />
          </div>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => {
              if (outputFrequency === RepeatingPattern.daily) {
                handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumDaily]);
              } else if (outputFrequency === RepeatingPattern.monthly) {
                handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumMonthly]);
              } else {
                handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumAnnually]);
              }
            }}
          >
            <Tooltip
              message={
                <div>
                  {outputFrequency === RepeatingPattern.daily
                    ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumDaily]
                    : outputFrequency === RepeatingPattern.monthly
                    ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumMonthly]
                    : getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumAnnually]}
                </div>
              }
              position="top"
            >
              <div>
                <FormattedMessage id="formula.sum" defaultMessage="SUM" />
              </div>
            </Tooltip>
          </button>
        </div>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => {
            if (outputFrequency === RepeatingPattern.daily) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgDaily]);
            } else if (outputFrequency === RepeatingPattern.monthly) {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgMonthly]);
            } else {
              handleActionClick(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgAnnually]);
            }
          }}
        >
          <Tooltip
            message={
              <div>
                {outputFrequency === RepeatingPattern.daily
                  ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgDaily]
                  : outputFrequency === RepeatingPattern.monthly
                  ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgMonthly]
                  : getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgAnnually]}
              </div>
            }
            position="top"
          >
            <div>
              <FormattedMessage id="formula.avg" defaultMessage="AVG" />
            </div>
          </Tooltip>
        </button>
      </div>
    </>
  );
};
